import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import i18n from '@/shared/i18n/i18n';
import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';
import { useUploadAsset, useMediaAssets } from '@/shared/media';
import type { MediaOwnerType } from '@/shared/media';

import { useContentPermissions } from '../../lib/useContentPermissions';
import {
  useContentPage,
  useContentPages,
  useCreateContentPage,
  useDeleteContentPage,
  usePublishContentPage,
  useReplaceContentBlocks,
  useUnpublishContentPage,
  useUpdateContentPage,
} from '../../hooks';
import {
  useContentSettings,
  useUpdateContentSettings,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
  toSettingsUpdate,
  SettingsCard,
  SwitchField,
  TextInputField,
  FeedbackSnackbar,
} from '@/features/store-settings';
import { CONTENT_PAGE_TEMPLATES, getContentPageTemplate, templateDraft } from '../../model/contentTemplates';
import type {
  BodyFormat,
  ContentBlock,
  ContentBlockItem,
  ContentBlockType,
  ContentPage,
  ContentPageType,
  ContentStatus,
  UpdatePageRequest,
} from '../../model/types';

// ── Constants ────────────────────────────────────────────────────────────────

/** Monospace font stack — JetBrains Mono is the OBZR design system monospace token. */
const MONOSPACE_FONT_FAMILY = 'JetBrains Mono, SFMono-Regular, Consolas, Liberation Mono, monospace';

// NOTE: every `label` below is an i18next KEY (e.g. 'content:options.pageType.all'), not literal
// display text — consumers must render `t(option.label)`. Translations live in
// public/locales/{lng}/content.json under the `options` key. Same pattern as
// `catalog/model/options.ts` (plan-009 reference implementation).
const PAGE_TYPE_OPTIONS: Array<{ value: ContentPageType | 'all'; label: string }> = [
  { value: 'all', label: 'content:options.pageType.all' },
  { value: 'SHOP_INFO_SECTION', label: 'content:options.pageType.shopInfoSection' },
  { value: 'LEGAL_DOCUMENT', label: 'content:options.pageType.legalDocument' },
  { value: 'GENERIC_PAGE', label: 'content:options.pageType.genericPage' },
];

const PAGE_STATUS_OPTIONS: Array<{ value: ContentStatus | 'all'; label: string }> = [
  { value: 'all', label: 'content:options.pageStatus.all' },
  { value: 'DRAFT', label: 'content:options.pageStatus.draft' },
  { value: 'PUBLISHED', label: 'content:options.pageStatus.published' },
  { value: 'ARCHIVED', label: 'content:options.pageStatus.archived' },
];

const BODY_FORMAT_OPTIONS: Array<{ value: BodyFormat; label: string }> = [
  { value: 'MARKDOWN', label: 'content:options.bodyFormat.markdown' },
  { value: 'PLAINTEXT', label: 'content:options.bodyFormat.plaintext' },
];

const BLOCK_TYPE_OPTIONS: Array<{ value: ContentBlockType; label: string }> = [
  { value: 'PAYMENT_METHODS', label: 'content:options.blockType.paymentMethods' },
  { value: 'DELIVERY_OPTIONS', label: 'content:options.blockType.deliveryOptions' },
  { value: 'RETURN_SECTIONS', label: 'content:options.blockType.returnSections' },
  { value: 'FAQ', label: 'content:options.blockType.faq' },
  { value: 'CONTACT_ROWS', label: 'content:options.blockType.contactRows' },
];

// `label`/`helperText` here are themselves i18next keys — `contentTemplates.ts` holds
// merchant-portal UI-chrome strings as keys (e.g. 'content:templates.shopOverview.label'), same as
// this file's other option dictionaries; only the *seed content* (title/subtitle/body/blocks) in
// that file is genuine tenant-authored Russian CMS content and stays untranslated.
const PAGE_TEMPLATE_OPTIONS = CONTENT_PAGE_TEMPLATES.map((template) => ({
  value: template.key,
  label: template.label,
  helperText: template.helperText,
}));

const LOCALE_OPTIONS = ['ru', 'en'];

const MEDIA_OWNER_TYPE_OPTIONS: Array<{ value: MediaOwnerType; label: string }> = [
  { value: 'CMS_PAGE', label: 'content:options.mediaOwnerType.cmsPage' },
  { value: 'LEGAL_DOCUMENT', label: 'content:options.mediaOwnerType.legalDocument' },
  { value: 'BANNER', label: 'content:options.mediaOwnerType.banner' },
];

// ── Pure utilities ───────────────────────────────────────────────────────────
// Module-level (non-component) functions resolve text via the i18next singleton, not
// `useTranslation` — see `catalog/lib/readiness.ts` for the same pattern.

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return i18n.t('common:error.unknown');
}

const emptyBlockItem = (): ContentBlockItem => ({ primary: '', secondary: '' });

function emptyBlock(blockType: ContentBlockType = 'FAQ'): ContentBlock {
  return {
    blockType,
    position: 0,
    items: [emptyBlockItem()],
  };
}

function clonePage(page: ContentPage): ContentPage {
  return {
    ...page,
    blocks: (page.blocks ?? []).map((block) => ({
      ...block,
      items: (block.items ?? []).map((item) => ({ ...item })),
    })),
  };
}

function formatBlocks(blocks: ContentBlock[] | null | undefined): string {
  const value = blocks ?? [];
  return JSON.stringify(value, null, 2);
}

/**
 * Parse and structurally validate a blocks JSON string.
 * Each block must have a string `blockType` and an array `items`.
 */
function parseBlocks(value: string): ContentBlock[] {
  if (!value.trim()) {
    return [];
  }
  const parsed: unknown = JSON.parse(value);
  if (!Array.isArray(parsed)) {
    throw new Error(i18n.t('content:errors.blocksMustBeArray'));
  }
  for (let i = 0; i < parsed.length; i++) {
    const block = parsed[i] as Record<string, unknown>;
    if (typeof block?.blockType !== 'string') {
      throw new Error(i18n.t('content:errors.blockTypeMustBeString', { index: i }));
    }
    if (!Array.isArray(block?.items)) {
      throw new Error(i18n.t('content:errors.blockItemsMustBeArray', { index: i, blockType: block.blockType }));
    }
  }
  return parsed as ContentBlock[];
}

function normalizeBlock(block: ContentBlock): ContentBlock {
  return {
    blockType: block.blockType,
    position: block.position ?? 0,
    items: block.items?.length ? block.items : [emptyBlockItem()],
  };
}

function sortBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return [...blocks]
    .map((block, index) => ({ block, index }))
    .sort((left, right) => {
      const leftPosition = left.block.position ?? 0;
      const rightPosition = right.block.position ?? 0;
      return leftPosition - rightPosition || left.index - right.index;
    })
    .map(({ block }) => block);
}

function cloneBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return blocks.map((block) => ({
    ...block,
    items: block.items.map((item) => ({ ...item })),
  }));
}

function serializeBlocks(blocks: ContentBlock[]): string {
  return formatBlocks(sortBlocks(blocks));
}

function statusColor(status: ContentStatus): 'default' | 'primary' | 'success' | 'warning' {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'warning';
    default:
      return 'default';
  }
}

function typeLabel(type: ContentPageType): string {
  return PAGE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

function statusLabel(status: ContentStatus): string {
  return PAGE_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function defaultMediaOwnerType(pageType: ContentPageType): MediaOwnerType {
  return pageType === 'LEGAL_DOCUMENT' ? 'LEGAL_DOCUMENT' : 'CMS_PAGE';
}

// ── Draft model ──────────────────────────────────────────────────────────────

interface PageDraftState {
  slug: string;
  type: ContentPageType;
  locale: string;
  title: string;
  subtitle: string;
  body: string;
  bodyFormat: BodyFormat;
  blocksJson: string;
  version: number | null;
}

function draftFromPage(page: ContentPage): PageDraftState {
  return {
    slug: page.slug,
    type: page.type,
    locale: page.locale,
    title: page.title ?? '',
    subtitle: page.subtitle ?? '',
    body: page.body ?? '',
    bodyFormat: page.bodyFormat,
    blocksJson: formatBlocks(page.blocks),
    version: page.version ?? null,
  };
}

function createDraftFromDefaults(): PageDraftState {
  return {
    slug: '',
    type: 'GENERIC_PAGE',
    locale: 'ru',
    title: '',
    subtitle: '',
    body: '',
    bodyFormat: 'MARKDOWN',
    blocksJson: formatBlocks([]),
    version: null,
  };
}

function draftFromTemplate(templateKey: string): PageDraftState | null {
  const template = getContentPageTemplate(templateKey);
  return template
    ? {
        ...templateDraft(template),
        version: null,
      }
    : null;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PageEditorSkeleton() {
  const { t } = useTranslation('content');
  return (
    <Stack spacing={2.5}>
      <LoadingState label={t('editor.skeletonLoading')} />
    </Stack>
  );
}

function PageFieldGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              aria-hidden
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 36,
                height: 36,
                borderRadius: 2,
                // H1 fix: use design-system token instead of hardcoded '#fff'
                color: 'common.white',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              }}
            >
              <ArticleRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6">{title}</Typography>
          </Stack>
          <Divider />
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BlocksJsonHint() {
  const { t } = useTranslation('content');
  return (
    <Alert severity="info" variant="outlined">
      {t('blocksEditor.hint')}
    </Alert>
  );
}

function MarkdownPreview({
  body,
  format,
  emptyLabel,
}: {
  body: string;
  format: BodyFormat;
  emptyLabel: string;
}) {
  if (!body.trim()) {
    return <Alert severity="info">{emptyLabel}</Alert>;
  }

  if (format === 'PLAINTEXT') {
    return (
      <Typography
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          // L5 fix: use shared constant instead of inline string literal
          fontFamily: MONOSPACE_FONT_FAMILY,
          fontSize: '0.875rem',
          lineHeight: 1.6,
        }}
      >
        {body}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 0, mb: 1, lineHeight: 1.2 },
        '& p': { mt: 0, mb: 1 },
        '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
        '& li + li': { mt: 0.5 },
        '& blockquote': {
          m: 0,
          pl: 1.5,
          py: 0.5,
          borderLeft: '3px solid',
          borderColor: 'divider',
          color: 'text.secondary',
        },
        '& code': {
          px: 0.5,
          py: 0.125,
          borderRadius: 0.75,
          bgcolor: 'action.hover',
          // L5 fix: use shared constant
          fontFamily: MONOSPACE_FONT_FAMILY,
        },
        '& pre': {
          p: 1.5,
          borderRadius: 1.5,
          overflow: 'auto',
          bgcolor: 'action.hover',
        },
        '& a': {
          color: 'primary.main',
          wordBreak: 'break-word',
        },
        '& img': {
          maxWidth: '100%',
          borderRadius: 1.5,
        },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </Box>
  );
}

function ContentMediaPanel({
  page,
  draftBodyFormat,
  disabled,
  onInsert,
}: {
  page: ContentPage | undefined;
  draftBodyFormat: BodyFormat;
  disabled: boolean;
  onInsert: (value: string) => void;
}) {
  const { t } = useTranslation('content');
  // C1 fix: import from @/shared/media instead of @/features/catalog/hooks
  const upload = useUploadAsset();
  const assetsQuery = useMediaAssets(
    {
      ownerType: page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE',
      ownerId: page?.slug,
      visibility: 'PUBLIC',
    },
    { enabled: Boolean(page) },
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ownerType, setOwnerType] = useState<MediaOwnerType>(page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE');

  useEffect(() => {
    setOwnerType(page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE');
  }, [page?.slug, page?.type]);

  const handleUpload = async (file: File | undefined) => {
    if (!file || !page) {
      return;
    }
    upload.mutate(
      { file, ownerType, ownerId: page.slug, visibility: 'PUBLIC' },
      {
        onSuccess: (asset) => {
          setUploadError(null);
          void assetsQuery.refetch();
          const snippet =
            draftBodyFormat === 'MARKDOWN'
              ? `![${file.name}](${asset.publicUrl})`
              : asset.publicUrl;
          onInsert(`\n\n${snippet}`);
        },
        onError: (error) => {
          setUploadError(error instanceof Error ? error.message : t('media.uploadFailed'));
        },
      },
    );
  };

  if (!page) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{t('media.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('media.description')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label={t('media.ownerTypeLabel')}
              value={ownerType}
              onChange={(event) => setOwnerType(event.target.value as MediaOwnerType)}
              disabled={disabled}
              sx={{ minWidth: 220 }}
            >
              {MEDIA_OWNER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || upload.isPending}
            >
              {t('media.uploadButton')}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                void handleUpload(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </Stack>
          {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
          {upload.isPending ? (
            <Typography variant="body2" color="text.secondary">
              {t('media.uploading')}
            </Typography>
          ) : null}

          <Divider />

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="subtitle2">{t('media.galleryTitle')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('media.assetCount', { count: assetsQuery.data?.length ?? 0 })}
              </Typography>
            </Stack>
            {assetsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                {t('media.loadingAssets')}
              </Typography>
            ) : assetsQuery.isError ? (
              <Alert severity="warning">{t('media.loadAssetsError')}</Alert>
            ) : (assetsQuery.data ?? []).length === 0 ? (
              <Alert severity="info">{t('media.noAssets')}</Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {(assetsQuery.data ?? []).map((asset) => (
                  <Card key={asset.assetId} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box
                      sx={{
                        aspectRatio: '16 / 9',
                        backgroundColor: 'action.hover',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={asset.publicUrl}
                        alt={asset.fileName ?? asset.publicAssetId}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {asset.fileName ?? asset.publicAssetId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {asset.publicAssetId}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const snippet =
                                draftBodyFormat === 'MARKDOWN'
                                  ? `![${asset.fileName ?? asset.publicAssetId}](${asset.publicUrl})`
                                  : asset.publicUrl;
                              onInsert(`\n\n${snippet}`);
                            }}
                            disabled={disabled}
                          >
                            {t('media.insertButton')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            // L4 fix: catch clipboard permission errors silently
                            onClick={() => void navigator.clipboard?.writeText(asset.publicUrl).catch(() => undefined)}
                          >
                            {t('media.copyUrlButton')}
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ContentBlocksEditor({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('content');
  const parsed = useMemo(() => {
    try {
      return { blocks: sortBlocks(parseBlocks(value).map(normalizeBlock)), error: null as string | null };
    } catch (error) {
      return { blocks: [] as ContentBlock[], error: getErrorMessage(error) };
    }
  }, [value]);

  const updateBlocks = (next: ContentBlock[]) => onChange(serializeBlocks(next));

  const updateBlock = (index: number, updater: (block: ContentBlock) => ContentBlock) => {
    const next = cloneBlocks(parsed.blocks).map((block, currentIndex) => (currentIndex === index ? updater(block) : block));
    updateBlocks(next);
  };

  const addBlock = () => {
    updateBlocks([...cloneBlocks(parsed.blocks), { ...emptyBlock(), position: parsed.blocks.length }]);
  };

  const removeBlock = (index: number) => {
    updateBlocks(
      parsed.blocks
        .filter((_, currentIndex) => currentIndex !== index)
        .map((block, currentIndex) => ({ ...block, position: currentIndex })),
    );
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= parsed.blocks.length) {
      return;
    }
    const next = cloneBlocks(parsed.blocks);
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    updateBlocks(next.map((block, currentIndex) => ({ ...block, position: currentIndex })));
  };

  const updateItem = (
    blockIndex: number,
    itemIndex: number,
    field: 'primary' | 'secondary',
    fieldValue: string,
  ) => {
    updateBlock(blockIndex, (block) => ({
      ...block,
      items: block.items.map((item, currentIndex) =>
        currentIndex === itemIndex
          ? {
              ...item,
              [field]: fieldValue,
            }
          : item,
      ),
    }));
  };

  const moveItem = (blockIndex: number, itemIndex: number, direction: -1 | 1) => {
    const targetIndex = itemIndex + direction;
    const block = parsed.blocks[blockIndex];
    if (!block || targetIndex < 0 || targetIndex >= block.items.length) {
      return;
    }
    const items = block.items.map((item) => ({ ...item }));
    const [moved] = items.splice(itemIndex, 1);
    items.splice(targetIndex, 0, moved);
    updateBlock(blockIndex, (current) => ({
      ...current,
      items,
    }));
  };

  const removeItem = (blockIndex: number, itemIndex: number) => {
    updateBlock(blockIndex, (current) => ({
      ...current,
      items: current.items.filter((_, currentIndex) => currentIndex !== itemIndex),
    }));
  };

  if (parsed.error) {
    return (
      <Stack spacing={1.5}>
        <Alert severity="warning">{t('blocksEditor.parseErrorPrefix', { details: parsed.error })}</Alert>
        <TextField
          label={t('blocksEditor.blocksJsonLabel')}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          fullWidth
          multiline
          minRows={16}
          // M4 fix: use MUI v7 slotProps instead of deprecated inputProps
          slotProps={{
            input: {
              style: {
                fontFamily: MONOSPACE_FONT_FAMILY,
                fontSize: '0.875rem',
              },
            },
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <BlocksJsonHint />
      <Stack spacing={2}>
        {parsed.blocks.map((block, blockIndex) => (
          <Card key={`${block.blockType}-${blockIndex}`} variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'flex-start' }}
                  justifyContent="space-between"
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flex: 1 }}>
                    <TextField
                      select
                      label={t('blocksEditor.blockTypeLabel')}
                      value={block.blockType}
                      onChange={(event) =>
                        updateBlock(blockIndex, (current) => ({
                          ...current,
                          blockType: event.target.value as ContentBlockType,
                        }))
                      }
                      disabled={disabled}
                      fullWidth
                    >
                      {BLOCK_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={t('blocksEditor.positionLabel')}
                      type="number"
                      value={block.position ?? 0}
                      onChange={(event) =>
                        updateBlock(blockIndex, (current) => ({
                          ...current,
                          position: Number(event.target.value || 0),
                        }))
                      }
                      disabled={disabled}
                      sx={{ maxWidth: 160 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    <IconButton
                      size="small"
                      onClick={() => moveBlock(blockIndex, -1)}
                      disabled={disabled || blockIndex === 0}
                      aria-label={t('blocksEditor.moveBlockUp')}
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveBlock(blockIndex, 1)}
                      disabled={disabled || blockIndex === parsed.blocks.length - 1}
                      aria-label={t('blocksEditor.moveBlockDown')}
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => removeBlock(blockIndex)}
                      disabled={disabled}
                    >
                      {t('blocksEditor.removeBlock')}
                    </Button>
                  </Stack>
                </Stack>
                <Stack spacing={1.5}>
                  {block.items.map((item, itemIndex) => (
                    <Box
                      key={`${block.blockType}-${blockIndex}-${itemIndex}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="caption" color="text.secondary">
                            {t('blocksEditor.itemLabel', { number: itemIndex + 1 })}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => moveItem(blockIndex, itemIndex, -1)}
                              disabled={disabled || itemIndex === 0}
                              aria-label={t('blocksEditor.moveItemUp')}
                            >
                              <ArrowUpwardRoundedIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveItem(blockIndex, itemIndex, 1)}
                              disabled={disabled || itemIndex === block.items.length - 1}
                              aria-label={t('blocksEditor.moveItemDown')}
                            >
                              <ArrowDownwardRoundedIcon fontSize="inherit" />
                            </IconButton>
                            {block.items.length > 1 ? (
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => removeItem(blockIndex, itemIndex)}
                                disabled={disabled}
                              >
                                {t('blocksEditor.removeItem')}
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            label={t('blocksEditor.primaryLabel')}
                            value={item.primary}
                            onChange={(event) =>
                              updateItem(blockIndex, itemIndex, 'primary', event.target.value)
                            }
                            disabled={disabled}
                            fullWidth
                          />
                          <TextField
                            label={t('blocksEditor.secondaryLabel')}
                            value={item.secondary ?? ''}
                            onChange={(event) =>
                              updateItem(blockIndex, itemIndex, 'secondary', event.target.value)
                            }
                            disabled={disabled}
                            fullWidth
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      updateBlock(blockIndex, (current) => ({
                        ...current,
                        items: [...current.items, emptyBlockItem()],
                      }))
                    }
                    disabled={disabled}
                  >
                    {t('blocksEditor.addItem')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={addBlock}
        disabled={disabled}
      >
        {t('blocksEditor.addBlock')}
      </Button>
      <TextField
        label={t('blocksEditor.blocksJsonLabel')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        fullWidth
        multiline
        minRows={12}
        // M4 fix: use MUI v7 slotProps instead of deprecated inputProps
        slotProps={{
          input: {
            style: {
              fontFamily: MONOSPACE_FONT_FAMILY,
              fontSize: '0.875rem',
            },
          },
        }}
      />
    </Stack>
  );
}

// ── Editor Drawer ────────────────────────────────────────────────────────────

const PAGE_EDITOR_DRAWER_WIDTH = {
  xs: '100vw',
  sm: '92vw',
  md: '88vw',
  lg: 'min(88vw, 1280px)',
};

// ── Content Settings Drawer ──────────────────────────────────────────────────

/** Narrower panel width — settings panels don't need the full-width editor canvas. */
const SETTINGS_DRAWER_WIDTH = {
  xs: '100vw',
  sm: 500,
  md: 540,
};

/**
 * Right-side settings panel for the Content domain, wired to
 * GET/PUT /api/v1/store/settings/content via the existing store-settings hooks.
 * Mirrors the visual language of ContentPageEditorDrawer (sticky header, scrollable
 * body, sticky footer with Save / Reset) using the store-settings SettingsCard /
 * SwitchField / TextInputField primitives so it stays consistent with every other
 * settings screen in the portal.
 */
function ContentSettingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('content');
  const { canView, canManage } = useDomainPermissions('content');
  const query = useContentSettings();
  const update = useUpdateContentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) return;
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('settingsDrawer.saveSuccess')),
      onError: (err) => {
        notifyError(err.message);
        // 409 Conflict means the version is stale — reload from server so the next
        // save attempt carries the correct version.
        if (err.status === 409) void query.refetch();
      },
    });
  };

  const disabled = !canManage;
  const draft = form.draft;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: SETTINGS_DRAWER_WIDTH,
              maxWidth: '100vw',
              height: '100vh',
              display: 'flex',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Sticky header ── */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              pb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SettingsRoundedIcon fontSize="small" color="primary" />
                <Typography variant="h5">{t('settingsDrawer.title')}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('settingsDrawer.subtitle')}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label={t('settingsDrawer.closeAria')}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          {/* ── Scrollable body ── */}
          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            {query.isLoading ? (
              <LoadingState label={t('settingsDrawer.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('settingsDrawer.unavailable.title')}
                message={query.error.message}
                onRetry={() => void query.refetch()}
              />
            ) : !canView ? (
              <ErrorState
                title={t('settingsDrawer.noAccess.title')}
                message={t('settingsDrawer.noAccess.message')}
              />
            ) : draft ? (
              <Stack spacing={2.5}>
                <SettingsCard title={t('settingsDrawer.modules.cardTitle')} icon={<ArticleRoundedIcon />}>
                  <SwitchField
                    label={t('settingsDrawer.modules.storefrontPages.label')}
                    description={t('settingsDrawer.modules.storefrontPages.description')}
                    checked={draft.storefrontPagesEnabled ?? false}
                    onChange={(value) => form.setField('storefrontPagesEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label={t('settingsDrawer.modules.legalContentPages.label')}
                    description={t('settingsDrawer.modules.legalContentPages.description')}
                    checked={draft.legalContentEnabled ?? false}
                    onChange={(value) => form.setField('legalContentEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label={t('settingsDrawer.modules.banners.label')}
                    description={t('settingsDrawer.modules.banners.description')}
                    checked={draft.bannersEnabled ?? false}
                    onChange={(value) => form.setField('bannersEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label={t('settingsDrawer.modules.landingPages.label')}
                    description={t('settingsDrawer.modules.landingPages.description')}
                    checked={draft.landingPagesEnabled ?? false}
                    onChange={(value) => form.setField('landingPagesEnabled', value)}
                    disabled={disabled}
                  />
                  <TextInputField
                    label={t('settingsDrawer.modules.publishingRule.label')}
                    description={t('settingsDrawer.modules.publishingRule.description')}
                    value={draft.publishingRule ?? ''}
                    onChange={(value) => form.setField('publishingRule', value)}
                    disabled={disabled}
                  />
                </SettingsCard>

                <SettingsCard
                  title={t('settingsDrawer.legalDisplay.cardTitle')}
                  icon={<GavelRoundedIcon />}
                  description={t('settingsDrawer.legalDisplay.cardDescription')}
                >
                  <SwitchField
                    label={t('settingsDrawer.legalDisplay.legalSection.label')}
                    description={t('settingsDrawer.legalDisplay.legalSection.description')}
                    checked={draft.legalSummaryBlockEnabled ?? false}
                    onChange={(value) => form.setField('legalSummaryBlockEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label={t('settingsDrawer.legalDisplay.legalDocLinks.label')}
                    description={t('settingsDrawer.legalDisplay.legalDocLinks.description')}
                    checked={draft.legalDocsRequired ?? false}
                    onChange={(value) => form.setField('legalDocsRequired', value)}
                    disabled={disabled}
                  />
                </SettingsCard>

                <SettingsCard
                  title={t('settingsDrawer.seo.cardTitle')}
                  icon={<TravelExploreRoundedIcon />}
                  description={t('settingsDrawer.seo.cardDescription')}
                >
                  <TextInputField
                    label={t('settingsDrawer.seo.defaultTitle.label')}
                    description={t('settingsDrawer.seo.defaultTitle.description')}
                    value={draft.seoMetadataDefaults?.title ?? ''}
                    onChange={(value) =>
                      form.setField('seoMetadataDefaults', {
                        ...draft.seoMetadataDefaults,
                        title: value || null,
                      })
                    }
                    disabled={disabled}
                  />
                  <TextInputField
                    label={t('settingsDrawer.seo.defaultDescription.label')}
                    description={t('settingsDrawer.seo.defaultDescription.description')}
                    value={draft.seoMetadataDefaults?.description ?? ''}
                    onChange={(value) =>
                      form.setField('seoMetadataDefaults', {
                        ...draft.seoMetadataDefaults,
                        description: value || null,
                      })
                    }
                    disabled={disabled}
                    multiline
                    rows={2}
                  />
                </SettingsCard>
              </Stack>
            ) : null}
          </Box>

          {/* ── Sticky footer — Save / Reset (only when the user can manage) ── */}
          {canManage && draft ? (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                onClick={form.reset}
                disabled={!form.dirty || update.isPending}
              >
                {t('settingsDrawer.reset')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!form.dirty || update.isPending}
              >
                {update.isPending ? t('settingsDrawer.saving') : t('settingsDrawer.save')}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <FeedbackSnackbar feedback={feedback} onClose={clear} />
    </>
  );
}

type ContentPageEditorMode = 'create' | 'edit';

function ContentPageEditorDrawer({
  open,
  mode,
  page,
  pageSlug,
  loading,
  error,
  draft,
  canManage,
  blocksError,
  operationError,
  onClose,
  onDraftChange,
  onApplyTemplate,
  onSave,
  onPublish,
  onUnpublish,
  onDelete,
  onReset,
  saving,
  publishing,
  unpublishing,
  deleting,
}: {
  open: boolean;
  mode: ContentPageEditorMode | null;
  page: ContentPage | undefined;
  pageSlug: string | undefined;
  loading: boolean;
  error: string | null;
  draft: PageDraftState;
  canManage: boolean;
  blocksError: string | null;
  /** Separate error slot for publish/unpublish/delete mutations (not block validation). */
  operationError: string | null;
  onClose: () => void;
  onDraftChange: (updater: (current: PageDraftState) => PageDraftState) => void;
  onApplyTemplate: (templateKey: string) => void;
  onSave: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onReset: () => void;
  saving: boolean;
  publishing: boolean;
  unpublishing: boolean;
  deleting: boolean;
}) {
  const { t } = useTranslation('content');
  const [templateKey, setTemplateKey] = useState('blank');
  // H2 fix: confirmation state for delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateKey('blank');
    }
  }, [mode, open]);

  const headerTitle =
    mode === 'create' ? t('editor.headerTitle.create') : page?.title ?? page?.slug ?? t('editor.headerTitle.fallback');
  const headerSubtitle =
    mode === 'create'
      ? t('editor.headerSubtitle.create')
      : page
        ? t('editor.headerSubtitle.existing', { slug: page.slug, locale: page.locale, type: t(typeLabel(page.type)) })
        : pageSlug
          ? t('editor.headerSubtitle.loading', { slug: pageSlug })
          : t('editor.headerSubtitle.none');

  const primaryLabel = mode === 'create' ? t('editor.primaryLabel.create') : t('editor.primaryLabel.edit');
  const body = (() => {
    if (mode === 'edit' && loading) {
      return <PageEditorSkeleton />;
    }

    if (mode === 'edit' && error) {
      return (
        <ErrorState
          title={t('editor.unavailable.title')}
          message={error}
        />
      );
    }

    return (
      <Stack spacing={2.5}>
        {page && page.publishedBody ? (
          <Alert severity="success" variant="outlined">
            {t('editor.publishedSnapshotExists')}
          </Alert>
        ) : null}

        {/* L6 fix: operation-level errors (publish/unpublish/delete) shown near action buttons */}
        {operationError ? (
          <Alert severity="error">{operationError}</Alert>
        ) : null}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {mode === 'edit' && canManage ? (
              <Button
                variant="contained"
                startIcon={<PublishRoundedIcon />}
                onClick={() => void onPublish()}
                disabled={publishing}
              >
                {t('editor.publish')}
              </Button>
            ) : null}
            {mode === 'edit' && canManage && page?.status === 'PUBLISHED' ? (
              <Button
                variant="outlined"
                startIcon={<UnpublishedRoundedIcon />}
                onClick={() => void onUnpublish()}
                disabled={unpublishing}
              >
                {t('editor.unpublish')}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="outlined"
                onClick={onReset}
                disabled={saving || publishing || unpublishing || deleting}
              >
                {t('editor.reset')}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="contained"
                onClick={() => void onSave()}
                disabled={saving || publishing || unpublishing || deleting || (!draft.slug.trim() && mode === 'create')}
              >
                {primaryLabel}
              </Button>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {mode === 'edit' && canManage ? (
              // H2 fix: open confirmation dialog before deleting
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleting}
              >
                {t('editor.delete')}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <PageFieldGroup title={t('editor.template.groupTitle')}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {t('editor.template.helper')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <TextField
                select
                label={t('editor.template.label')}
                value={templateKey}
                onChange={(event) => setTemplateKey(event.target.value)}
                disabled={!canManage}
                fullWidth
              >
                <MenuItem value="blank">{t('editor.template.blankOption')}</MenuItem>
                {PAGE_TEMPLATE_OPTIONS.map((template) => (
                  <MenuItem key={template.value} value={template.value}>
                    {t(template.label)}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={() => onApplyTemplate(templateKey)}
                disabled={!canManage || templateKey === 'blank'}
                sx={{ minWidth: { xs: '100%', sm: 160 } }}
              >
                {t('editor.template.applyButton')}
              </Button>
            </Stack>
          </Stack>
        </PageFieldGroup>

        <PageFieldGroup title={t('editor.details.groupTitle')}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label={t('editor.details.slugLabel')}
                value={draft.slug}
                onChange={(event) => onDraftChange((current) => ({ ...current, slug: event.target.value }))}
                disabled={mode === 'edit' || !canManage}
                fullWidth
                helperText={mode === 'edit' ? t('editor.details.slugHelperEdit') : t('editor.details.slugHelperCreate')}
              />
              <TextField
                select
                label={t('editor.details.localeLabel')}
                value={draft.locale}
                onChange={(event) => onDraftChange((current) => ({ ...current, locale: event.target.value }))}
                disabled={!canManage}
                fullWidth
              >
                {LOCALE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label={t('editor.details.titleLabel')}
                value={draft.title}
                onChange={(event) => onDraftChange((current) => ({ ...current, title: event.target.value }))}
                disabled={!canManage}
                fullWidth
              />
              <TextField
                select
                label={t('editor.details.pageTypeLabel')}
                value={draft.type}
                onChange={(event) =>
                  onDraftChange((current) => ({ ...current, type: event.target.value as ContentPageType }))
                }
                disabled={!canManage}
                fullWidth
              >
                {PAGE_TYPE_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label={t('editor.details.subtitleLabel')}
              value={draft.subtitle}
              onChange={(event) => onDraftChange((current) => ({ ...current, subtitle: event.target.value }))}
              disabled={!canManage}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                label={t('editor.details.bodyFormatLabel')}
                value={draft.bodyFormat}
                onChange={(event) =>
                  onDraftChange((current) => ({ ...current, bodyFormat: event.target.value as BodyFormat }))
                }
                disabled={!canManage}
                fullWidth
              >
                {BODY_FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label={t('editor.details.versionLabel')} value={draft.version ?? 0} disabled fullWidth />
            </Stack>
            <TextField
              label={t('editor.details.bodyLabel')}
              value={draft.body}
              onChange={(event) => onDraftChange((current) => ({ ...current, body: event.target.value }))}
              disabled={!canManage}
              fullWidth
              multiline
              minRows={10}
            />
          </Stack>
        </PageFieldGroup>

        <PageFieldGroup title={t('editor.preview.groupTitle')}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {t('editor.preview.helper')}
            </Typography>
            <MarkdownPreview
              body={draft.body}
              format={draft.bodyFormat}
              emptyLabel={t('editor.preview.emptyLabel')}
            />
          </Stack>
        </PageFieldGroup>

        {mode === 'edit' ? (
          <ContentMediaPanel
            page={page}
            draftBodyFormat={draft.bodyFormat}
            disabled={!canManage}
            onInsert={(value) =>
              onDraftChange((current) => ({
                ...current,
                body: `${current.body}${value}`,
              }))
            }
          />
        ) : null}

        <PageFieldGroup title={t('editor.blocksGroup.groupTitle')}>
          <Stack spacing={2}>
            {blocksError ? <Alert severity="error">{blocksError}</Alert> : null}
            <ContentBlocksEditor
              value={draft.blocksJson}
              disabled={!canManage}
              onChange={(value) => onDraftChange((current) => ({ ...current, blocksJson: value }))}
            />
          </Stack>
        </PageFieldGroup>

        {page && (page.publishedBody || page.body) ? (
          <Card>
            <CardContent>
              <Typography variant="h6">{t('editor.publishedSnapshot.title')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('editor.publishedSnapshot.helper')}
              </Typography>
              <MarkdownPreview
                body={page.publishedBody ?? page.body ?? ''}
                format={page.bodyFormat}
                emptyLabel={t('editor.publishedSnapshot.emptyLabel')}
              />
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    );
  })();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              width: PAGE_EDITOR_DRAWER_WIDTH,
              maxWidth: '100vw',
              height: '100vh',
              display: 'flex',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              pb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5">{headerTitle}</Typography>
                {page ? <Chip size="small" variant="outlined" label={`v${page.version ?? 0}`} /> : null}
                {page ? <Chip size="small" color={statusColor(page.status)} label={t(statusLabel(page.status))} /> : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {headerSubtitle}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label={t('editor.closeAria')}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>{body}</Box>
        </Box>
      </Drawer>

      {/* H2 fix: delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-page-dialog-title"
      >
        <DialogTitle id="delete-page-dialog-title">{t('editor.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('editor.deleteDialog.body', { slug: page?.slug ?? pageSlug })}{' '}
            {page?.status === 'PUBLISHED' ? t('editor.deleteDialog.publishedWarning') : null}{' '}
            {t('editor.deleteDialog.cannotUndo')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => {
              setDeleteConfirmOpen(false);
              void onDelete();
            }}
          >
            {t('editor.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

/** Merchant CMS workspace: list pages, open a page, edit draft content and blocks, then publish. */
export function ContentPagesPage() {
  const { t } = useTranslation('content');
  const { canView, canManage } = useContentPermissions();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ContentPageType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [locale, setLocale] = useState(searchParams.get('locale') ?? 'ru');
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<PageDraftState>(createDraftFromDefaults());
  const [blocksError, setBlocksError] = useState<string | null>(null);
  // L6 fix: separate error state for publish/unpublish/delete operations
  const [operationError, setOperationError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  // M2 fix: track whether the user has unsaved edits to avoid overwriting them on background refetch
  const [isDirty, setIsDirty] = useState(false);
  // Settings gear: controls the Content domain settings drawer
  const [settingsOpen, setSettingsOpen] = useState(false);

  const listQuery = useContentPages(
    {
      type: typeFilter === 'all' ? undefined : typeFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
      locale,
    },
    canView,
  );
  const pages = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const filteredPages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return pages;
    }
    return pages.filter(
      (page) =>
        page.slug.toLowerCase().includes(term) ||
        (page.title ?? '').toLowerCase().includes(term) ||
        t(typeLabel(page.type)).toLowerCase().includes(term),
    );
  }, [pages, search, t]);
  const pagedPages = useMemo(
    () => filteredPages.slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage),
    [filteredPages, rowsPerPage, tablePage],
  );

  const selectedSlug = params.slug;
  const pageQuery = useContentPage(selectedSlug, locale, canView && Boolean(selectedSlug));
  const selectedPage = pageQuery.data;
  const editorMode: ContentPageEditorMode | null = createOpen ? 'create' : selectedSlug ? 'edit' : null;
  const editorOpen = Boolean(editorMode);

  const createMutation = useCreateContentPage();
  const updateMutation = useUpdateContentPage();
  const deleteMutation = useDeleteContentPage();
  const publishMutation = usePublishContentPage();
  const unpublishMutation = useUnpublishContentPage();
  const replaceBlocksMutation = useReplaceContentBlocks();

  // M3 fix: merge locale into existing search params instead of replacing all params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('locale', locale);
        return next;
      },
      { replace: true },
    );
  }, [locale, setSearchParams]);

  // M2 fix: reset dirty flag when navigating to a different page
  useEffect(() => {
    setIsDirty(false);
  }, [selectedSlug]);

  // M2 fix: only reseed draft from server if the user hasn't made unsaved edits
  useEffect(() => {
    if (isDirty) return;
    if (selectedPage) {
      setDraft(draftFromPage(clonePage(selectedPage)));
      setBlocksError(null);
      setCreateOpen(false);
    } else if (!selectedSlug) {
      setDraft(createDraftFromDefaults());
      setBlocksError(null);
    }
  }, [selectedPage, selectedSlug, isDirty]);

  useEffect(() => {
    setTablePage(0);
  }, [locale, search, statusFilter, typeFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredPages.length / rowsPerPage) - 1);
    if (tablePage > lastPage) {
      setTablePage(lastPage);
    }
  }, [filteredPages.length, rowsPerPage, tablePage]);

  // M2 fix: wrapper that marks draft as dirty before updating
  const handleDraftChange = (updater: (current: PageDraftState) => PageDraftState) => {
    setIsDirty(true);
    setDraft(updater);
  };

  const handleSelectPage = (slug: string) => {
    setCreateOpen(false);
    navigate(`/content/pages/${slug}?locale=${locale}`);
  };

  const openCreateDrawer = () => {
    setBlocksError(null);
    setOperationError(null);
    setDraft(createDraftFromDefaults());
    setIsDirty(false);
    setCreateOpen(true);
  };

  const closeEditor = () => {
    if (createOpen) {
      setCreateOpen(false);
      return;
    }
    if (selectedSlug) {
      navigate(`/content/pages?locale=${locale}`);
    }
  };

  /**
   * C2 fix: two-phase save with immediate version update after the first mutation.
   *
   * If the text-content mutation (updatePage/createPage) succeeds but the blocks
   * mutation fails, the server has already incremented the page version.  We update
   * draft.version immediately after the first mutation so that a retry does not hit
   * a 409 Conflict on the second attempt.
   */
  const persistDraft = async () => {
    const activeSlug = selectedSlug ?? draft.slug.trim();
    if (!activeSlug) {
      throw new Error(t('errors.selectOrCreatePage'));
    }
    setBlocksError(null);
    const blocks = parseBlocks(draft.blocksJson);
    const updateBody: UpdatePageRequest = {
      title: draft.title || null,
      subtitle: draft.subtitle || null,
      // Use ?? null (not || null) so that an empty string '' is sent as-is, telling the backend
      // to clear the body. The || operator converts '' to null which the backend interprets as
      // "no change", causing an empty body to never be persisted (B1 fix).
      body: draft.body ?? null,
      bodyFormat: draft.bodyFormat,
      version: draft.version,
    };
    const nextPage =
      editorMode === 'create'
        ? await createMutation.mutateAsync({
            slug: activeSlug,
            type: draft.type,
            locale: draft.locale,
            title: updateBody.title,
            subtitle: updateBody.subtitle,
            body: updateBody.body,
            bodyFormat: updateBody.bodyFormat,
          })
        : await updateMutation.mutateAsync({
            slug: activeSlug,
            locale,
            body: updateBody,
          });

    // Immediately record the new server version so that a retry after a partial
    // blocks failure does not send the stale version and receive a 409 Conflict.
    setDraft((prev) => ({ ...prev, version: nextPage.version ?? prev.version }));

    await replaceBlocksMutation.mutateAsync({
      slug: nextPage.slug,
      locale: editorMode === 'create' ? draft.locale : locale,
      body: { blocks },
    });
    return nextPage;
  };

  const handleSaveDraft = async () => {
    try {
      const nextPage = await persistDraft();
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
      if (editorMode === 'create') {
        setCreateOpen(false);
        navigate(`/content/pages/${nextPage.slug}?locale=${nextPage.locale}`);
      }
    } catch (error) {
      setBlocksError(getErrorMessage(error));
    }
  };

  const handlePublish = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      await persistDraft();
      const nextPage = await publishMutation.mutateAsync({ slug: selectedSlug, locale });
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
    } catch (error) {
      // L6 fix: publish errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleUnpublish = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      const nextPage = await unpublishMutation.mutateAsync({ slug: selectedSlug, locale });
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
    } catch (error) {
      // L6 fix: unpublish errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      await deleteMutation.mutateAsync({ slug: selectedSlug, locale });
      navigate('/content/pages');
    } catch (error) {
      // L6 fix: delete errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleResetDraft = () => {
    if (editorMode === 'create') {
      setDraft(createDraftFromDefaults());
      setBlocksError(null);
      setOperationError(null);
      setIsDirty(false);
      return;
    }
    if (selectedPage) {
      setDraft(draftFromPage(clonePage(selectedPage)));
      setBlocksError(null);
      setOperationError(null);
      setIsDirty(false);
    }
  };

  if (!canView) {
    return (
      <PageContainer title={t('page.title')} subtitle={t('page.subtitle')}>
        <ErrorState
          title={t('access.title')}
          message={t('access.pagesMessage')}
        />
      </PageContainer>
    );
  }

  if (listQuery.isLoading) {
    return (
      <PageContainer title={t('page.title')} subtitle={t('page.subtitle')}>
        <LoadingState label={t('loading')} />
      </PageContainer>
    );
  }

  if (listQuery.isError) {
    return (
      <PageContainer title={t('page.title')} subtitle={t('page.subtitle')}>
        <ErrorState
          title={t('loadError.title')}
          message={listQuery.error.message}
          onRetry={() => void listQuery.refetch()}
        />
      </PageContainer>
    );
  }

  return (
      <PageContainer
      title={t('page.title')}
      subtitle={t('page.subtitleDetailed')}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t('settingsTooltip')}>
            <IconButton
              onClick={() => setSettingsOpen(true)}
              aria-label={t('settingsAria')}
              size="small"
            >
              <SettingsRoundedIcon />
            </IconButton>
          </Tooltip>
          {canManage ? (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreateDrawer}
            >
              {t('newPageButton')}
            </Button>
          ) : null}
        </Stack>
      }
    >
      {!canManage ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5 }}>
          {t('readOnlyBanner')}
        </Alert>
      ) : null}

      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label={t('filters.searchLabel')}
            placeholder={t('filters.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
          <TextField
            select
            size="small"
            label={t('filters.localeLabel')}
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
            sx={{ width: { xs: '100%', md: 150 } }}
          >
            {LOCALE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('filters.typeLabel')}
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value as ContentPageType | 'all')}
            sx={{ width: { xs: '100%', md: 180 } }}
          >
            {PAGE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('filters.statusLabel')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as ContentStatus | 'all')}
            sx={{ width: { xs: '100%', md: 180 } }}
          >
            {PAGE_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h6">{t('pagesCard.title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pagesCard.countLabel', { count: filteredPages.length, locale })}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {/* L3 fix: add aria-label for screen readers */}
                  <IconButton
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    size="small"
                    aria-label={t('pagesCard.optionsAria')}
                  >
                    <MoreVertRoundedIcon />
                  </IconButton>
                  <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    {/* H4 fix: actually trigger a refetch when Refresh is clicked */}
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        void listQuery.refetch();
                      }}
                    >
                      {t('pagesCard.refresh')}
                    </MenuItem>
                  </Menu>
                </Stack>
              </Stack>

              {filteredPages.length === 0 ? (
                <EmptyState
                  title={t('pagesCard.empty.title')}
                  description={t('pagesCard.empty.description')}
                  action={
                    canManage ? (
                      <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDrawer}>
                        {t('newPageButton')}
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('pagesCard.table.page')}</TableCell>
                          <TableCell>{t('pagesCard.table.status')}</TableCell>
                          <TableCell align="right">{t('pagesCard.table.open')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedPages.map((page) => {
                          const selected = page.slug === selectedSlug;
                          return (
                            <TableRow
                              key={`${page.slug}:${page.locale}`}
                              hover
                              selected={selected}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleSelectPage(page.slug)}
                            >
                              <TableCell>
                                <Stack spacing={0.25}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {page.title ?? page.slug}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {page.slug} · {t(typeLabel(page.type))} · {page.locale}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" color={statusColor(page.status)} label={t(statusLabel(page.status))} />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  component={RouterLink}
                                  to={`/content/pages/${page.slug}?locale=${locale}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'outlined'}
                                  startIcon={<EditRoundedIcon />}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {t('pagesCard.openButton')}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredPages.length}
                    page={tablePage}
                    onPageChange={(_, nextPage) => setTablePage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setTablePage(0);
                    }}
                    rowsPerPageOptions={[5, 8, 12, 20]}
                  />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <ContentPageEditorDrawer
        open={editorOpen}
        mode={editorMode}
        page={selectedPage}
        pageSlug={selectedSlug}
        loading={pageQuery.isLoading}
        error={pageQuery.isError ? pageQuery.error.message : null}
        draft={draft}
        canManage={canManage}
        blocksError={blocksError}
        operationError={operationError}
        onClose={closeEditor}
        onDraftChange={handleDraftChange}
        onApplyTemplate={(templateKey) => {
          const next = draftFromTemplate(templateKey);
          if (next) {
            setDraft(next);
            setIsDirty(true);
            setBlocksError(null);
          }
        }}
        onSave={() => void handleSaveDraft()}
        onPublish={() => void handlePublish()}
        onUnpublish={() => void handleUnpublish()}
        onDelete={() => void handleDelete()}
        onReset={handleResetDraft}
        saving={updateMutation.isPending || createMutation.isPending || replaceBlocksMutation.isPending}
        publishing={publishMutation.isPending}
        unpublishing={unpublishMutation.isPending}
        deleting={deleteMutation.isPending}
      />

      <ContentSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </PageContainer>
  );
}
